// @ts-nocheck
import { useState, useMemo, useCallback } from "react";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KiiDialogLayout } from "@/components/kii/kii-dialog-layout";
import { KiiImageThumbnail } from "@/components/kii/kii-image-thumbnail";
import { KiiImageUploadZone } from "@/components/kii/kii-image-upload-zone";
import { cn } from "@/lib/utils";
import type { NMPhoto } from "@/components/kii/kii-editor";
import type { TranscriptObservation } from "@/shared/schema";
import type { FileUploadProgress } from "@/hooks/useFileUpload";

interface PhotoManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  observationIndex: number;
  observation: TranscriptObservation;
  allPhotos: NMPhoto[];
  allObservations: TranscriptObservation[];
  isNMDocument: boolean;
  onAddPhoto: (observationIndex: number, photoId: string) => void;
  onPermanentDelete: (photoId: string) => Promise<void>;
  onUploadPhotos: (files: File[]) => Promise<void>;
  uploadProgress: FileUploadProgress[];
  isUploading: boolean;
}

export function PhotoManagementDialog({
  open,
  onOpenChange,
  observationIndex,
  observation,
  allPhotos,
  allObservations,
  isNMDocument,
  onAddPhoto,
  onPermanentDelete,
  onUploadPhotos,
  uploadProgress,
  isUploading,
}: PhotoManagementDialogProps) {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Compute used photo IDs across all observations
  const usedPhotoIds = useMemo(() => {
    return new Set(allObservations.flatMap(obs => obs.photo_ids));
  }, [allObservations]);

  // Available photos: in DB but not assigned to any observation
  const availablePhotos = useMemo(() => {
    return allPhotos
      .filter(p => !usedPhotoIds.has(p.id))
      .sort((a, b) => {
        const aTime = a.metadata?.exif?.DateTimeOriginal || a.metadata?.exif?.DateTime || "";
        const bTime = b.metadata?.exif?.DateTimeOriginal || b.metadata?.exif?.DateTime || "";
        return aTime.localeCompare(bTime);
      });
  }, [allPhotos, usedPhotoIds]);

  // Get timestamps of photos already in the current observation for proximity matching
  const observationPhotoTimestamps = useMemo(() => {
    const timestamps: number[] = [];
    for (const photoId of observation.photo_ids) {
      const photo = allPhotos.find(p => p.id === photoId);
      const dateStr = photo?.metadata?.exif?.DateTimeOriginal || photo?.metadata?.exif?.DateTime;
      if (dateStr) {
        // EXIF dates are "YYYY:MM:DD HH:MM:SS"
        const parsed = Date.parse(dateStr.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3"));
        if (!isNaN(parsed)) timestamps.push(parsed);
      }
    }
    return timestamps;
  }, [observation.photo_ids, allPhotos]);

  // Check if a photo's timestamp is near (within 60s) any existing observation photo
  const isSuggestedPhoto = useCallback((photo: NMPhoto): boolean => {
    if (observationPhotoTimestamps.length === 0) return false;
    const dateStr = photo.metadata?.exif?.DateTimeOriginal || photo.metadata?.exif?.DateTime;
    if (!dateStr) return false;
    const parsed = Date.parse(dateStr.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3"));
    if (isNaN(parsed)) return false;
    return observationPhotoTimestamps.some(ts => Math.abs(ts - parsed) <= 60000);
  }, [observationPhotoTimestamps]);

  const handleAddPhoto = (photoId: string) => {
    onAddPhoto(observationIndex, photoId);
  };

  const handleDeleteClick = (photoId: string) => {
    setDeleteConfirmId(photoId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await onPermanentDelete(deleteConfirmId);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  return (
    <>
      <KiiDialogLayout
        open={open}
        onOpenChange={onOpenChange}
        title={`Manage Photos - ${observation.title || `Observation ${observationIndex + 1}`}`}
        description="Add existing photos or upload new ones to this observation."
        size="2xl"
      >
        <div className="mt-4 space-y-6">
          {/* Available Photos Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">
              Available Photos
              {availablePhotos.length > 0 && (
                <span className="text-muted-foreground font-normal ml-1">
                  ({availablePhotos.length})
                </span>
              )}
            </h3>

            {availablePhotos.length > 0 ? (
              <div className="kii-photo-grid">
                {availablePhotos.map(photo => (
                  <KiiImageThumbnail
                    key={photo.id}
                    photo={photo}
                    className={cn(isSuggestedPhoto(photo) && "kii-photo-suggested")}
                    actions={
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleAddPhoto(photo.id)}
                          className="h-7 text-xs flex-1"
                        >
                          <Icon name={iconNames.add} size={14} className="mr-1" />
                          Add
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteClick(photo.id)}
                          className="h-7 w-7 p-0"
                        >
                          <Icon name={iconNames.delete} size={14} />
                        </Button>
                      </>
                    }
                  />
                ))}
              </div>
            ) : (
              !isUploading && (
                <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
                  <Icon name={iconNames.image} size={32} className="mx-auto mb-2 opacity-40" />
                  <p>No available photos</p>
                  <p className="text-xs mt-1">
                    All photos are assigned to observations, or upload new photos below.
                  </p>
                </div>
              )
            )}
          </div>

          {/* Upload Photos Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Upload Photos</h3>
            <KiiImageUploadZone
              onUpload={onUploadPhotos}
              uploadProgress={uploadProgress}
              isUploading={isUploading}
            />
          </div>
        </div>
      </KiiDialogLayout>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Photo</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this photo from the document. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground"
            >
              {isDeleting ? "Deleting..." : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
